// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { 
  CategorizedResults as CategorizedResultsType, 
  PaperWithStatus, 
  ResultFilter,
  CategoryInfo
} from '../types/resultCategorization';
import { 
  CATEGORY_CONFIG, 
  filterCategorizedResults, 
  getCategoryCount, 
  isCategoryEmpty,
  getRelevanceExplanation
} from '../types/resultCategorization';
import { PaperListItem } from './PaperListItem';
import './CategorizedResults.css';

export interface LibraryCategorizedResultsProps {
  categorizedResults: CategorizedResultsType;
  onPaperClick: (pmid: string) => void;
  onRemove: (pmid: string) => void;
  selectedInputSource?: string | null;
  // Filter props
  activeFilter?: ResultFilter;
  onFilterChange?: (filter: ResultFilter) => void;
  // State persistence
  expandedCategories?: Set<string>;
  onCategoryToggle?: (category: string) => void;
}

/**
 * Specialized categorized results component for library tab with remove functionality
 */
export const LibraryCategorizedResults: React.FC<LibraryCategorizedResultsProps> = ({
  categorizedResults,
  onPaperClick,
  onRemove,
  selectedInputSource,
  activeFilter = 'all',
  onFilterChange,
  expandedCategories = new Set(['highlyRelevant', 'somewhatRelevant']),
  onCategoryToggle
}) => {
  // Apply filter to results
  const filteredResults = filterCategorizedResults(categorizedResults, activeFilter);
  
  // Get non-empty categories for display
  const visibleCategories = (Object.keys(CATEGORY_CONFIG) as Array<keyof CategorizedResultsType>)
    .filter(category => !isCategoryEmpty(filteredResults, category));

  const handleCategoryToggle = (category: string) => {
    if (onCategoryToggle) {
      onCategoryToggle(category);
    }
  };

  const renderCategoryHeader = (category: keyof CategorizedResultsType, config: CategoryInfo) => {
    const count = getCategoryCount(filteredResults, category);
    const isExpanded = expandedCategories.has(category);
    
    return (
      <div 
        className={`categorized-results__category-header categorized-results__category-header--${config.color}`}
        onClick={() => handleCategoryToggle(category)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCategoryToggle(category);
          }
        }}
        aria-expanded={isExpanded}
        aria-controls={`category-${category}`}
      >
        <div className="categorized-results__category-title">
          <span className="categorized-results__category-icon">{config.icon}</span>
          <span className="categorized-results__category-label">{config.label}</span>
          <span className="categorized-results__category-count">({count})</span>
        </div>
        <div className="categorized-results__category-description">
          {config.description}
        </div>
        <div className="categorized-results__category-toggle">
          {isExpanded ? '▼' : '▶'}
        </div>
      </div>
    );
  };

  const renderCategoryContent = (category: keyof CategorizedResultsType, papers: PaperWithStatus[]) => {
    const isExpanded = expandedCategories.has(category);
    
    if (!isExpanded) return null;

    return (
      <div 
        className="categorized-results__category-content"
        id={`category-${category}`}
        role="region"
        aria-labelledby={`category-header-${category}`}
      >
        {papers.map(paper => {
          // Apply input source filter if active
          const isHiddenByFilter = selectedInputSource && 
            (!paper.aiBrief || !paper.aiBrief.potentialInputSources?.includes(selectedInputSource as any));

          if (isHiddenByFilter) return null;

          return (
            <div key={paper.pmid} className="categorized-results__paper-container">
              <div className="evidence-drawer__library-item">
                <PaperListItem
                  paper={paper}
                  onClick={() => onPaperClick(paper.pmid)}
                  triageBrief={paper.aiBrief}
                  triageError={paper.triageError}
                  isLoading={false}
                  highlightInputSource={selectedInputSource}
                />
                <button
                  className="evidence-drawer__remove-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(paper.pmid);
                  }}
                  aria-label={`Remove ${paper.title}`}
                >
                  Remove
                </button>
              </div>
              {/* Show relevance explanation for relevant papers */}
              {(category === 'highlyRelevant' || category === 'somewhatRelevant') && paper.aiBrief && (
                <div className="categorized-results__relevance-explanation">
                  {getRelevanceExplanation(paper)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFilterControls = () => {
    const filterOptions: Array<{ value: ResultFilter; label: string; description: string }> = [
      { value: 'all', label: 'All Papers', description: 'Show all papers regardless of analysis status' },
      { value: 'relevant', label: 'Relevant Only', description: 'Show only papers with relevant parameters' },
      { value: 'not_relevant', label: 'Not Relevant', description: 'Show papers with no relevant parameters' },
      { value: 'failed', label: 'Failed Analysis', description: 'Show papers that failed to analyze' },
      { value: 'not_analyzed', label: 'Not Analyzed', description: 'Show papers not yet analyzed' }
    ];

    return (
      <div className="categorized-results__filter-controls">
        <div className="categorized-results__filter-label">Filter by outcome:</div>
        <div className="categorized-results__filter-buttons">
          {filterOptions.map(option => (
            <button
              key={option.value}
              className={`categorized-results__filter-button ${
                activeFilter === option.value ? 'categorized-results__filter-button--active' : ''
              }`}
              onClick={() => onFilterChange?.(option.value)}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (visibleCategories.length === 0) {
    return (
      <div className="categorized-results__empty">
        <div className="categorized-results__empty-icon">No data</div>
        <div className="categorized-results__empty-title">No saved papers in this category yet</div>
        <div className="categorized-results__empty-description">
          {activeFilter === 'all' 
            ? 'No papers have been analyzed yet.'
            : `No papers match the "${CATEGORY_CONFIG[activeFilter as keyof typeof CATEGORY_CONFIG]?.label || activeFilter}" filter.`
          }
        </div>
      </div>
    );
  }

  return (
    <div className="categorized-results">
      {onFilterChange && renderFilterControls()}
      
      <div className="categorized-results__categories">
        {visibleCategories.map(category => {
          const config = CATEGORY_CONFIG[category];
          const papers = filteredResults[category];
          
          return (
            <div key={category} className="categorized-results__category">
              {renderCategoryHeader(category, config)}
              {renderCategoryContent(category, papers)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
